// NIGHT-DIRECTSTAFF-2026-10-25d §1: Candidates -> Pipeline. Six columns, each DERIVED from data the CRM already has:
//   placed     a placement confirmed / started / ending / extended, or the stage Active Contract / Completed
//   offered    an open offer (status offered), or the stage Negotiating
//   presented  the stage Submitted
//   screening  the stages Docs Collecting / Submission Ready / Needs Resubmission
//   contacted  the stages Outreach Sent / Contacted / Scheduling Call / Engaged
//   new        the stage New Lead (or none)
// (the first that applies, in that order). On Hold / Partnership / Inactive candidates are off the board (counted below it).
// A MOVE is a real status change only between the stage columns (new / contacted / screening / presented): it sets the
// candidate's stage to the column's own stage. Offered and Placed come from offers and placements, so they are read only --
// moving there (or out of there) says what to do instead. Test and merged candidates never show.
export const COLUMNS = [
  { id: 'new', label: 'New lead', stage: 'New Lead' },
  { id: 'contacted', label: 'Contacted', stage: 'Contacted' },
  { id: 'screening', label: 'Screening', stage: 'Docs Collecting' },
  { id: 'presented', label: 'Presented', stage: 'Submitted' },
  { id: 'offered', label: 'Offered', stage: null, why: 'Offered comes from an offer: make one on the candidate (Offers).' },
  { id: 'placed', label: 'Placed', stage: null, why: 'Placed comes from a placement: add it on the candidate (Placements).' },
];
const STAGE_COL = { 'New Lead': 'new', 'Outreach Sent': 'contacted', Contacted: 'contacted', 'Scheduling Call': 'contacted', Engaged: 'contacted',
  'Docs Collecting': 'screening', 'Submission Ready': 'screening', 'Needs Resubmission': 'screening', Submitted: 'presented', Negotiating: 'offered',
  'Active Contract': 'placed', Completed: 'placed' };
export const OFF_BOARD = new Set(['On Hold', 'Partnership', 'Inactive']);
const PLACED = new Set(['confirmed', 'started', 'ending', 'extended']);
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const isTest = (c) => !!c && (!!c.test_marked_at || (Array.isArray(c.tags) && c.tags.some((x) => s(x).toLowerCase() === 'test')));

/** one candidate -> its column id (or 'off' for On Hold / Partnership / Inactive) */
export function columnOf(c, { offers = [], placements = [] } = {}) {
  const id = String(c.id);
  if ((placements || []).some((p) => String(p.candidate_id) === id && PLACED.has(s(p.status)))) return 'placed';
  if ((offers || []).some((o) => String(o.candidate_id) === id && s(o.status) === 'offered')) return 'offered';
  const st = s(c.stage);
  if (OFF_BOARD.has(st)) return 'off';
  return STAGE_COL[st] || 'new';
}
/** -> { columns: [{ ...col, cards: [candidate] }], off: n } with the filters applied */
export function buildBoard({ candidates = [], offers = [], placements = [], discipline = '', state = '' } = {}) {
  const shown = (candidates || []).filter((c) => !isTest(c) && !c.merged_into)
    .filter((c) => !discipline || s(c.specialty).toUpperCase() === discipline.toUpperCase())
    .filter((c) => !state || s(c.state).toUpperCase() === state.toUpperCase() || (Array.isArray(c.licenses) && c.licenses.map((x) => s(x).toUpperCase()).includes(state.toUpperCase())));
  const by = new Map(COLUMNS.map((k) => [k.id, []]));
  let off = 0;
  for (const c of shown) { const col = columnOf(c, { offers, placements }); if (col === 'off') off += 1; else by.get(col).push(c); }
  for (const list of by.values()) list.sort((a, b) => s(b.updated_at || b.created_at).localeCompare(s(a.updated_at || a.created_at)));
  return { columns: COLUMNS.map((k) => ({ ...k, cards: by.get(k.id) })), off };
}
/** Can this candidate move from one column to another? -> { stage } (the new stage) | { why } (it cannot, and what to do) */
export function moveFor(c, from, to) {
  if (from === to) return { why: 'It is already there.' };
  const src = COLUMNS.find((k) => k.id === from); const dst = COLUMNS.find((k) => k.id === to);
  if (!dst) return { why: 'Unknown column.' };
  if (!dst.stage) return { why: dst.why };
  if (src && !src.stage) return { why: from === 'placed' ? 'Placed comes from a placement: change it on the candidate (Placements).' : 'Offered comes from an open offer: settle it on the candidate (Offers).' };
  return { stage: dst.stage };
}
export const disciplinesOf = (cands) => [...new Set((cands || []).map((c) => s(c.specialty).toUpperCase()).filter(Boolean))].sort();
export const statesOf = (cands) => [...new Set((cands || []).map((c) => s(c.state).toUpperCase()).filter((x) => /^[A-Z]{2}$/.test(x)))].sort();
